import React, { useState, useEffect, useCallback } from 'react'
import { Select, Spin, Form } from 'antd'
import { getMonetisationPujaApi, showErrorToast } from '@/utils/helper'
import { useAppContext } from '@/components/AppContext'
import { TempleDetailsListItem } from '@a4b/api'
import InfiniteScrollWrapper from '@/components/ui/InfiniteScrollWrapper'

const PAGE_SIZE = 20

const TempleNameSuggestions: React.FC = () => {
  const { networkInstance, app, languageState } = useAppContext()
  const monetisationPujaApi = getMonetisationPujaApi(app, networkInstance)

  // Callback ref to capture Ant Design's virtual list holder element
  const [virtualListEl, setVirtualListEl] = useState<HTMLElement | null>(null)
  const virtualListRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const virtualList = node.querySelector<HTMLElement>(
        '.rc-virtual-list-holder'
      )
      setVirtualListEl(virtualList)
    } else {
      setVirtualListEl(null)
    }
  }, [])


  const [options, setOptions] = useState<{ value: string; label: string }[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false)
  const [searchText, setSearchText] = useState<string>('')

  // Determine if the user has typed something not in the options list
  const isCustomValue =
    searchText.trim().length > 0 &&
    !options.some(opt => opt.value.toLowerCase() === searchText.trim().toLowerCase())

  // Build the final options list, appending a custom entry if the user is typing something new
  const allOptions = isCustomValue
    ? [
      ...options,
      { value: searchText.trim(), label: `Use "${searchText.trim()}"` },
    ]
    : options

  // Fetch a specific page and append/replace options
  const fetchSuggestions = useCallback(
    async (page: number, append: boolean = false) => {
      if (append) {
        setIsFetchingMore(true)
      } else {
        setIsLoading(true)
      }

      try {
        const response = await monetisationPujaApi.storesApi.listTemplesName(
          languageState?.monetisationLanguage || 'en',
          PAGE_SIZE,
          page,
        )
        const { data: items, meta } = response.data
        const newOptions = items.map(
          (item: TempleDetailsListItem) => ({
            value: item.attributes.temple_slug,
            label: item.attributes.temple_slug,
          }),
        )

        setOptions(prev => (append ? [...prev, ...newOptions] : newOptions))
        setTotalPages(meta.pagination.pageCount)
      } catch (error) {
        showErrorToast(error)
        console.error('Error fetching temple name suggestions:', error)
      } finally {
        setIsLoading(false)
        setIsFetchingMore(false)
      }
    },
    [languageState?.monetisationLanguage, monetisationPujaApi.storesApi],
  )

  // Load first page on mount and when language changes
  useEffect(() => {
    setCurrentPage(1)
    setOptions([])
    fetchSuggestions(1)
  }, [fetchSuggestions])

  return (
    <Form.Item
      name={["location_details", "temple_details", "name"]}
      label="Name"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      rules={[{ required: false, message: 'Please input your Temple name' }]}
    >
      <Select
        showSearch
        allowClear
        placeholder="Select or type temple name"
        style={{ width: '100%' }}
        onSearch={value => setSearchText(value)}
        filterOption={(input, option) => {
          // Don't filter out the custom "Use ..." option
          if (option?.value === input) return true
          return (option?.label ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }}
        notFoundContent={isLoading ? <Spin size="small" /> : 'No temples found'}
        loading={isLoading && options.length === 0}
        optionFilterProp="label"
        listHeight={256}
        dropdownRender={menu => (
          <InfiniteScrollWrapper
            currentPage={currentPage}
            totalPages={totalPages}
            isFetchingMore={isFetchingMore}
            onLoadMore={(page) => {
              setCurrentPage(page)
              fetchSuggestions(page, true)
            }}
            scrollContainerEl={virtualListEl}
          >
            <div ref={virtualListRef}>
              {menu}
            </div>
          </InfiniteScrollWrapper>
        )}
        options={allOptions}
      />
    </Form.Item>
  )
}

export default TempleNameSuggestions